import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, RefreshCw, Calendar, TrendingUp, ArrowLeftRight, BarChart3, ChevronDown, List, Tag, ChevronLeft, ChevronRight, Trash2, Lock, Search, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminIncome = () => {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [allTransactions, setAllTransactions] = useState(null);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionStatusFilter, setTransactionStatusFilter] = useState('completed');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [activePartnerTab, setActivePartnerTab] = useState('deisy');
  const [mainTab, setMainTab] = useState('current');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [verifyReference, setVerifyReference] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const [configRes, summaryRes, statsRes, rateRes, transactionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/income/config`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/income/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/income/monthly-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/admin/income/exchange-rate`),
        axios.get(`${BACKEND_URL}/api/admin/income/transactions?page=1&limit=50&status_filter=${transactionStatusFilter}&destination_filter=${destinationFilter}&product_filter=${productFilter}&date_from=${dateFromFilter}&date_to=${dateToFilter}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConfig(configRes.data);
      setSummary({...summaryRes.data, usd_to_cop_rate: rateRes.data.usd_to_cop});
      setMonthlyStats(statsRes.data);
      setAllTransactions(transactionsRes.data);
      
      // Extraer lista de productos únicos
      if (transactionsRes.data?.all_products) {
        setAvailableProducts(transactionsRes.data.all_products);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionsPage = async (page, statusFilter = transactionStatusFilter, destFilter = destinationFilter, prodFilter = productFilter, dateFrom = dateFromFilter, dateTo = dateToFilter) => {
    const token = localStorage.getItem('session_token');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/income/transactions?page=${page}&limit=50&status_filter=${statusFilter}&destination_filter=${destFilter}&product_filter=${prodFilter}&date_from=${dateFrom}&date_to=${dateTo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllTransactions(res.data);
      setTransactionsPage(page);
      
      // Actualizar lista de productos
      if (res.data?.all_products) {
        setAvailableProducts(res.data.all_products);
      }
    } catch (error) {
      toast.error('Error al cargar transacciones');
    }
  };

  const handleStatusFilterChange = (newFilter) => {
    setTransactionStatusFilter(newFilter);
    setTransactionsPage(1);
    loadTransactionsPage(1, newFilter, destinationFilter, productFilter, dateFromFilter, dateToFilter);
  };

  const handleDestinationFilterChange = (newFilter) => {
    setDestinationFilter(newFilter);
    setTransactionsPage(1);
    loadTransactionsPage(1, transactionStatusFilter, newFilter, productFilter, dateFromFilter, dateToFilter);
  };

  const handleProductFilterChange = (newFilter) => {
    setProductFilter(newFilter);
    setTransactionsPage(1);
    loadTransactionsPage(1, transactionStatusFilter, destinationFilter, newFilter, dateFromFilter, dateToFilter);
  };

  const handleDateFilterChange = (from, to) => {
    setDateFromFilter(from);
    setDateToFilter(to);
    setTransactionsPage(1);
    loadTransactionsPage(1, transactionStatusFilter, destinationFilter, productFilter, from, to);
  };

  const clearAllFilters = () => {
    setTransactionStatusFilter('completed');
    setDestinationFilter('all');
    setProductFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setTransactionsPage(1);
    loadTransactionsPage(1, 'completed', 'all', 'all', '', '');
  };

  const handleDeleteTransaction = async (transactionId, isDeletable) => {
    if (!isDeletable) {
      toast.error('Esta transacción tiene ingreso real y no se puede eliminar');
      return;
    }
    if (!window.confirm('¿Eliminar esta transacción de prueba?')) return;
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/income/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transacción eliminada');
      loadTransactionsPage(transactionsPage);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteAllTestTransactions = async () => {
    if (!window.confirm('⚠️ Eliminar TODAS las transacciones de prueba?')) return;
    if (!window.confirm('Esta acción no se puede deshacer. ¿Confirmar?')) return;
    
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.delete(`${BACKEND_URL}/api/admin/income/transactions-all-test`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.deleted_count} transacciones eliminadas`);
      loadTransactionsPage(1);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleVerifyWompiPayment = async () => {
    if (!verifyReference.trim()) {
      toast.error('Ingresa la referencia del pago');
      return;
    }
    
    setVerifying(true);
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/verify-wompi-payment`, 
        { reference: verifyReference.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'APPROVED') {
        toast.success(`✅ Pago APROBADO - Usuario actualizado a ${response.data.new_level || 'nivel correspondiente'}`);
        setVerifyReference('');
        loadData();
        loadTransactionsPage(1);
      } else if (response.data.status === 'PENDING') {
        toast.warning('⏳ El pago aún está pendiente en Wompi');
      } else if (response.data.status === 'already_completed') {
        toast.info('Este pago ya fue procesado anteriormente');
      } else {
        toast.error(`Estado del pago: ${response.data.status}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al verificar pago');
    } finally {
      setVerifying(false);
    }
  };

  const handleResetRotation = async (startPartner) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/income/reset-rotation`, 
        { start_partner: startPartner },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${startPartner === 'deisy' ? 'Deisy' : 'Karen'} está activa ahora`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar rotación');
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (currency === 'COP') {
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount);
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const getMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  const currentPartner = config?.current_active_partner;

  return (
    <div className="min-h-screen bg-[#09090b] py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <span className="text-4xl">💰</span> Gestión de Ingresos
            </h1>
            <p className="text-zinc-400 mt-1">Administra los ingresos de Deisy y Karen</p>
          </div>
          <Button onClick={loadData} className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
        </div>

        {/* Tabs Principales */}
        <div className="flex gap-2 bg-zinc-900 p-1 rounded-xl w-fit border border-zinc-800">
          {['current', 'statistics', 'transactions'].map((tab) => (
            <button
              key={tab}
              onClick={() => setMainTab(tab)}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                mainTab === tab
                  ? 'bg-pink-600 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              {tab === 'current' ? '📅 Mes Actual' : tab === 'statistics' ? '📊 Estadísticas' : '📋 Transacciones'}
            </button>
          ))}
        </div>

        {mainTab === 'current' ? (
          <>
            {/* Rotación Actual */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowLeftRight className="w-5 h-5" />
                    <span className="text-pink-200 text-sm uppercase tracking-wider">Rotación Semanal</span>
                    <button
                      onClick={async () => {
                        const token = localStorage.getItem('session_token');
                        try {
                          const newAutoRotate = !config?.auto_rotation_enabled;
                          await axios.put(`${BACKEND_URL}/api/admin/income/config`, 
                            { ...config, auto_rotation_enabled: newAutoRotate },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setConfig({ ...config, auto_rotation_enabled: newAutoRotate });
                          toast.success(newAutoRotate ? 'Rotación automática ON' : 'Rotación automática OFF');
                        } catch (error) {
                          toast.error('Error');
                        }
                      }}
                      className={`ml-2 px-3 py-1 rounded-full text-xs font-bold ${
                        config?.auto_rotation_enabled ? 'bg-green-400 text-green-900' : 'bg-zinc-900/20'
                      }`}
                    >
                      {config?.auto_rotation_enabled ? '✓ AUTO' : '○ MANUAL'}
                    </button>
                  </div>
                  <h2 className="text-3xl font-bold">
                    {currentPartner === 'deisy' ? '👩 Deisy' : '👩 Karen'}
                  </h2>
                  <p className="text-pink-200">
                    Recibiendo pagos • {config?.days_remaining} días restantes
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-pink-200 mb-2">Próximo cambio:</div>
                  <div className="text-lg font-bold">
                    {config?.next_rotation_date ? new Date(config.next_rotation_date).toLocaleDateString('es-CO', {
                      weekday: 'short', day: 'numeric', month: 'short'
                    }) : '-'}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" className="bg-zinc-900/20 hover:bg-zinc-900/30 text-white border-0" onClick={() => handleResetRotation('deisy')}>
                      Activar Deisy
                    </Button>
                    <Button size="sm" className="bg-zinc-900/20 hover:bg-zinc-900/30 text-white border-0" onClick={() => handleResetRotation('karen')}>
                      Activar Karen
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen de Ingresos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Deisy */}
              <div className="bg-zinc-900 border border-pink-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">👩</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Deisy</h3>
                    <p className="text-xs text-zinc-500">{summary?.summary?.deisy?.transaction_count || 0} transacciones</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">USD:</span>
                    <span className="font-bold text-green-400">{formatCurrency(summary?.summary?.deisy?.total_usd || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">COP:</span>
                    <span className="font-medium text-zinc-300">{formatCurrency(summary?.summary?.deisy?.total_cop || 0, 'COP')}</span>
                  </div>
                </div>
              </div>

              {/* Karen */}
              <div className="bg-zinc-900 border border-purple-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">👩</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Karen</h3>
                    <p className="text-xs text-zinc-500">{summary?.summary?.karen?.transaction_count || 0} transacciones</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">USD:</span>
                    <span className="font-bold text-green-400">{formatCurrency(summary?.summary?.karen?.total_usd || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">COP:</span>
                    <span className="font-medium text-zinc-300">{formatCurrency(summary?.summary?.karen?.total_cop || 0, 'COP')}</span>
                  </div>
                </div>
              </div>

              {/* Sin Asignar */}
              <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">⏳</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Sin Asignar</h3>
                    <p className="text-xs text-zinc-500">{summary?.summary?.unassigned?.transaction_count || 0} transacciones</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">USD:</span>
                    <span className="font-bold text-amber-400">{formatCurrency(summary?.summary?.unassigned?.total_usd || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">COP:</span>
                    <span className="font-medium text-zinc-300">{formatCurrency(summary?.summary?.unassigned?.total_cop || 0, 'COP')}</span>
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="bg-gradient-to-br from-pink-600/20 to-purple-600/20 border border-pink-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-pink-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">Total</h3>
                    <p className="text-xs text-zinc-500">{summary?.summary?.total?.transaction_count || 0} transacciones</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">USD:</span>
                    <span className="font-bold text-green-400 text-xl">{formatCurrency(summary?.summary?.total?.total_usd || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 text-sm">COP:</span>
                    <span className="font-medium text-zinc-300">{formatCurrency(summary?.summary?.total?.total_cop || 0, 'COP')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tasa de Cambio */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-blue-400" />
                <span className="text-blue-300">Tasa: <strong className="text-white">1 USD = {formatCurrency(summary?.usd_to_cop_rate || 4200, 'COP')}</strong></span>
              </div>
              <span className="text-xs text-blue-400">exchangerate-api.com</span>
            </div>

            {/* Tabs de Socias */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="flex border-b border-zinc-800">
                <button
                  onClick={() => setActivePartnerTab('deisy')}
                  className={`flex-1 py-4 px-6 font-medium transition-all ${
                    activePartnerTab === 'deisy' 
                      ? 'bg-pink-500/10 text-pink-400 border-b-2 border-pink-500' 
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  👩 Deisy
                </button>
                <button
                  onClick={() => setActivePartnerTab('karen')}
                  className={`flex-1 py-4 px-6 font-medium transition-all ${
                    activePartnerTab === 'karen' 
                      ? 'bg-purple-500/10 text-purple-400 border-b-2 border-purple-500' 
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  👩 Karen
                </button>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                  <CreditCard className="w-5 h-5 text-pink-500" />
                  Pasarelas de {activePartnerTab === 'deisy' ? 'Deisy' : 'Karen'}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* PayPal */}
                  <div className="bg-zinc-800/50 border border-blue-500/30 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                          <span className="text-white font-bold text-lg">P</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white">PayPal</h4>
                          <p className="text-sm text-zinc-500">USD Internacional</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        config?.gateway_status?.[activePartnerTab]?.paypal
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      }`}>
                        {config?.gateway_status?.[activePartnerTab]?.paypal ? '✓ Activo' : '⏳ Pendiente'}
                      </span>
                    </div>
                  </div>

                  {/* Wompi */}
                  <div className={`bg-zinc-800/50 border rounded-xl p-5 ${
                    config?.gateway_status?.[activePartnerTab]?.wompi ? 'border-green-500/30' : 'border-amber-500/30'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          config?.gateway_status?.[activePartnerTab]?.wompi ? 'bg-green-600' : 'bg-zinc-600'
                        }`}>
                          <span className="text-white font-bold text-lg">W</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white">Wompi</h4>
                          <p className="text-sm text-zinc-500">PSE, Nequi, Tarjeta (COP)</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        config?.gateway_status?.[activePartnerTab]?.wompi
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      }`}>
                        {config?.gateway_status?.[activePartnerTab]?.wompi ? '✓ Activo' : '⏳ Pendiente'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Últimas Transacciones */}
                <div className="mt-8">
                  <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-pink-500" />
                    Últimas Transacciones
                  </h4>
                  
                  {summary?.transactions?.[activePartnerTab]?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-zinc-800">
                          <tr>
                            <th className="text-left p-3 rounded-l-lg text-zinc-400">Fecha</th>
                            <th className="text-left p-3 text-zinc-400">Tipo</th>
                            <th className="text-left p-3 text-zinc-400">Email</th>
                            <th className="text-right p-3 rounded-r-lg text-zinc-400">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.transactions[activePartnerTab].map((txn, idx) => (
                            <tr key={idx} className="border-b border-zinc-800">
                              <td className="p-3 text-zinc-400">
                                {new Date(txn.created_at).toLocaleDateString('es-CO')}
                              </td>
                              <td className="p-3">
                                <span className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded text-xs">
                                  {txn.metadata?.package_id || txn.type || 'Pago'}
                                </span>
                              </td>
                              <td className="p-3 text-zinc-400">{txn.email || txn.user_email || '-'}</td>
                              <td className="p-3 text-right font-bold text-green-400">
                                {formatCurrency(txn.display_amount || txn.amount, txn.display_currency || 'USD')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      Sin transacciones para {activePartnerTab === 'deisy' ? 'Deisy' : 'Karen'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : mainTab === 'statistics' ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-6 h-6 text-pink-500" />
              <h2 className="text-2xl font-bold text-white">Estadísticas Mensuales</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-br from-green-600/20 to-green-500/10 border border-green-500/30 rounded-xl p-6">
                <div className="text-green-400 text-sm mb-1">Total Histórico USD</div>
                <div className="text-3xl font-bold text-white">{formatCurrency(monthlyStats?.total_all_time_usd || 0)}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-600/20 to-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                <div className="text-blue-400 text-sm mb-1">Total Histórico COP</div>
                <div className="text-3xl font-bold text-white">{formatCurrency(monthlyStats?.total_all_time_cop || 0, 'COP')}</div>
              </div>
            </div>

            <div className="space-y-4">
              {monthlyStats?.months?.length > 0 ? (
                monthlyStats.months.map((month) => (
                  <div key={month.month_key} className="border border-zinc-800 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedMonth(expandedMonth === month.month_key ? null : month.month_key)}
                      className="w-full p-4 bg-zinc-800/50 hover:bg-zinc-800 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-pink-500" />
                        <span className="font-bold text-lg text-white capitalize">{getMonthName(month.month_key)}</span>
                        <span className="text-sm text-zinc-500">({month.transaction_count} txns)</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-bold text-green-400">{formatCurrency(month.total_usd)}</div>
                          <div className="text-sm text-zinc-500">{formatCurrency(month.total_cop, 'COP')}</div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${expandedMonth === month.month_key ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    
                    {expandedMonth === month.month_key && (
                      <div className="p-4 border-t border-zinc-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="border border-pink-500/30 rounded-xl p-4 bg-pink-500/5">
                            <h4 className="font-bold text-white mb-3">👩 Deisy</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-zinc-500">USD:</span>
                                <span className="font-bold text-green-400">{formatCurrency(month.deisy_usd || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-zinc-500">COP:</span>
                                <span className="text-zinc-300">{formatCurrency(month.deisy_cop || 0, 'COP')}</span>
                              </div>
                            </div>
                          </div>
                          <div className="border border-purple-500/30 rounded-xl p-4 bg-purple-500/5">
                            <h4 className="font-bold text-white mb-3">👩 Karen</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-zinc-500">USD:</span>
                                <span className="font-bold text-green-400">{formatCurrency(month.karen_usd || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-zinc-500">COP:</span>
                                <span className="text-zinc-300">{formatCurrency(month.karen_cop || 0, 'COP')}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Sin datos históricos</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* TRANSACCIONES */
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            {/* Verificar Pago de Wompi */}
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <h3 className="font-bold text-white">Verificar Pago de Wompi</h3>
              </div>
              <p className="text-zinc-400 text-sm mb-3">
                Si un usuario pagó pero su cuenta no se actualizó, ingresa la referencia del pago para verificarlo y actualizar su nivel automáticamente.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verifyReference}
                  onChange={(e) => setVerifyReference(e.target.value)}
                  placeholder="Ej: plan_premium_24571dae"
                  className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:border-green-500 outline-none"
                />
                <Button 
                  onClick={handleVerifyWompiPayment}
                  disabled={verifying}
                  className="bg-green-600 hover:bg-green-500 text-white gap-2"
                >
                  <Search className="w-4 h-4" />
                  {verifying ? 'Verificando...' : 'Verificar'}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <List className="w-6 h-6 text-pink-500" />
                <h2 className="text-2xl font-bold text-white">Transacciones</h2>
                {allTransactions?.pagination && (
                  <span className="text-sm text-zinc-500 bg-zinc-800 px-3 py-1 rounded-full border border-zinc-700">
                    {allTransactions.pagination.total} en total
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Filtros de destino */}
                <div className="flex gap-1 bg-zinc-800 p-1 rounded-lg">
                  {[
                    { value: 'all', label: '👥 Todas', activeClass: 'bg-pink-500/20 text-pink-400 ring-1 ring-pink-500/50' },
                    { value: 'deisy', label: '👤 Deisy', activeClass: 'bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/50' },
                    { value: 'karen', label: '👤 Karen', activeClass: 'bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/50' }
                  ].map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => handleDestinationFilterChange(filter.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        destinationFilter === filter.value
                          ? filter.activeClass
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                {/* Filtros de estado */}
                <div className="flex gap-1 bg-zinc-800 p-1 rounded-lg">
                  {[
                    { value: 'completed', label: '✅ Pagados', activeClass: 'bg-green-500/20 text-green-400 ring-1 ring-green-500/50' },
                    { value: 'pending', label: '⏳ Pendientes', activeClass: 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/50' },
                    { value: 'failed', label: '❌ Fallidos', activeClass: 'bg-red-500/20 text-red-400 ring-1 ring-red-500/50' },
                    { value: 'all', label: '📋 Todos', activeClass: 'bg-zinc-500/20 text-zinc-300 ring-1 ring-zinc-500/50' }
                  ].map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => handleStatusFilterChange(filter.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        transactionStatusFilter === filter.value
                          ? filter.activeClass
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                {/* Botón eliminar pruebas - solo visible cuando hay transacciones eliminables */}
                {(transactionStatusFilter === 'pending' || transactionStatusFilter === 'all') && 
                 allTransactions?.transactions?.some(t => t.is_deletable) && (
                  <Button onClick={handleDeleteAllTestTransactions} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 gap-2 text-xs">
                    <Trash2 className="w-3 h-3" />
                    Limpiar pruebas
                  </Button>
                )}
              </div>
            </div>

            {/* Filtros avanzados: Fecha y Producto */}
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-medium text-white">Filtros Avanzados</span>
                {(dateFromFilter || dateToFilter || productFilter !== 'all') && (
                  <button 
                    onClick={clearAllFilters}
                    className="ml-auto text-xs text-pink-400 hover:text-pink-300"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Filtro de fecha desde */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Desde</label>
                  <input
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => handleDateFilterChange(e.target.value, dateToFilter)}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:border-pink-500 outline-none"
                  />
                </div>
                {/* Filtro de fecha hasta */}
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => handleDateFilterChange(dateFromFilter, e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:border-pink-500 outline-none"
                  />
                </div>
                {/* Filtro de producto */}
                <div className="md:col-span-2">
                  <label className="block text-xs text-zinc-500 mb-1">Producto</label>
                  <select
                    value={productFilter}
                    onChange={(e) => handleProductFilterChange(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:border-pink-500 outline-none"
                  >
                    <option value="all">Todos los productos</option>
                    {availableProducts.map((product, idx) => (
                      <option key={idx} value={product}>{product}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Totales filtrados */}
            {allTransactions?.totals && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {/* COP - Wompi */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-xs text-zinc-500 mb-1">💜 Wompi Bruto (COP)</p>
                  <p className="text-lg font-bold text-white">${allTransactions.totals.total_cop?.toLocaleString('es-CO')}</p>
                </div>
                <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
                  <p className="text-xs text-purple-400 mb-1">💜 Wompi Neto (COP)</p>
                  <p className="text-lg font-bold text-purple-400">${allTransactions.totals.total_cop_net?.toLocaleString('es-CO')}</p>
                  <p className="text-xs text-zinc-500 mt-1">-2.65% + $700 + IVA</p>
                </div>
                {/* USD - PayPal */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-xs text-zinc-500 mb-1">💙 PayPal Bruto (USD)</p>
                  <p className="text-lg font-bold text-white">${(allTransactions.totals.total_usd || 0).toFixed(2)}</p>
                </div>
                <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                  <p className="text-xs text-blue-400 mb-1">💙 PayPal Neto (USD)</p>
                  <p className="text-lg font-bold text-blue-400">${(allTransactions.totals.total_usd_net || 0).toFixed(2)}</p>
                  <p className="text-xs text-zinc-500 mt-1">-5.4% + $0.30</p>
                </div>
              </div>
            )}
            
            {allTransactions?.transactions?.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-800">
                      <tr>
                        <th className="text-left p-3 rounded-l-lg text-zinc-400">Fecha</th>
                        <th className="text-left p-3 text-zinc-400">Usuario</th>
                        <th className="text-left p-3 text-zinc-400">Producto</th>
                        <th className="text-center p-3 text-zinc-400">Método</th>
                        <th className="text-center p-3 text-zinc-400">Destino</th>
                        <th className="text-center p-3 text-zinc-400">Descuento</th>
                        <th className="text-right p-3 text-zinc-400">Monto Bruto</th>
                        <th className="text-right p-3 text-zinc-400">Monto Neto</th>
                        <th className="text-center p-3 text-zinc-400">Estado</th>
                        <th className="text-center p-3 rounded-r-lg text-zinc-400">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allTransactions.transactions.map((txn, idx) => (
                        <tr key={idx} className={`border-b border-zinc-800 hover:bg-zinc-800/50 ${txn.is_deletable ? 'bg-amber-500/5' : ''}`}>
                          <td className="p-3 text-zinc-400">
                            {txn.created_at ? new Date(txn.created_at).toLocaleDateString('es-CO', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            }) : '-'}
                          </td>
                          <td className="p-3">
                            <div className="font-medium text-white">{txn.user_name || '-'}</div>
                            <div className="text-xs text-zinc-500">{txn.user_email}</div>
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-1 bg-pink-500/20 text-pink-400 rounded-lg text-xs font-medium">
                              {txn.product_name}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              txn.payment_method === 'PayPal' ? 'bg-blue-500/20 text-blue-400' :
                              txn.payment_method === 'Gratuito' ? 'bg-green-500/20 text-green-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              {txn.payment_method}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              txn.account_destination === 'Deisy' ? 'bg-pink-500/20 text-pink-400' :
                              txn.account_destination === 'Karen' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {txn.account_destination}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {txn.has_discount ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <div className="flex items-center gap-1">
                                  <Tag className="w-3 h-3 text-green-400" />
                                  <span className="text-xs font-medium text-green-400">{txn.discount_code}</span>
                                </div>
                                {txn.discount_amount > 0 && (
                                  <span className="text-xs text-green-300 bg-green-500/10 px-2 py-0.5 rounded">
                                    -${txn.discount_amount} USD
                                  </span>
                                )}
                              </div>
                            ) : <span className="text-zinc-400">-</span>}
                          </td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-white">
                              {formatCurrency(txn.currency === 'COP' ? (txn.amount_cop || txn.amount) : txn.amount, txn.currency || 'USD')}
                            </div>
                          </td>
                          <td className="p-3 text-right">
                            <div className="font-bold text-green-400">
                              {txn.amount_net ? (
                                formatCurrency(txn.amount_net, txn.currency || 'USD')
                              ) : (
                                formatCurrency(txn.currency === 'COP' ? (txn.amount_cop || txn.amount) : txn.amount, txn.currency || 'USD')
                              )}
                            </div>
                            {txn.commission && (
                              <div className="text-xs text-red-400">
                                -{formatCurrency(txn.commission, txn.currency || 'USD')}
                              </div>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              txn.payment_status === 'paid' || txn.payment_status === 'completed'
                                ? 'bg-green-500/20 text-green-400'
                                : txn.payment_status === 'pending'
                                  ? 'bg-amber-500/20 text-amber-400'
                                  : 'bg-red-500/20 text-red-400'
                            }`}>
                              {txn.payment_status === 'paid' || txn.payment_status === 'completed' ? '✓ Pagado' : 
                               txn.payment_status === 'pending' ? '⏳ Pendiente' : txn.payment_status}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {txn.is_deletable ? (
                              <button onClick={() => handleDeleteTransaction(txn.transaction_id, txn.is_deletable)}
                                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <span className="p-2 text-green-400 inline-flex"><Lock className="w-4 h-4" /></span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {allTransactions.pagination.total_pages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-800">
                    <div className="text-sm text-zinc-500">
                      Página {transactionsPage} de {allTransactions.pagination.total_pages}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => loadTransactionsPage(transactionsPage - 1)}
                        disabled={transactionsPage <= 1} className="bg-zinc-800 border-zinc-700 text-white">
                        <ChevronLeft className="w-4 h-4" /> Anterior
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => loadTransactionsPage(transactionsPage + 1)}
                        disabled={transactionsPage >= allTransactions.pagination.total_pages} className="bg-zinc-800 border-zinc-700 text-white">
                        Siguiente <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <List className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Sin transacciones</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminIncome;
